import { AgingBucket } from 'src/types';
import {
  STATUS_PALETTE,
  ERROR_STRONG_DARK_CHART,
  type StatusToken,
} from 'src/theme/status-palette';

// ---------------------------------------------------------------------------
// Aging bucket styling, derived from the semantic status palette.
// Used by AgingBucketCards (card styling) and AgingBucketBadge (table pills).
//
// Bucket → status-token mapping:
//   current  → neutral   (in-term / default)
//   1_30     → warning   (mild concern)
//   31_60    → error     (standard alarm)
//   60_plus  → error     (intensified — see ERROR_STRONG below)
//
// See STATUS-PALETTE.md for the palette's shape and seed rules.
// ---------------------------------------------------------------------------

export const AGING_BUCKET_STATUS_MAP: Record<AgingBucket, StatusToken> = {
  current: 'neutral',
  '1_30': 'warning',
  '31_60': 'error',
  '60_plus': 'error',
};

// `60_plus` renders as a saturated filled red card + white text, **in both
// color modes**, to distinguish severity from `31_60`. STATUS_PALETTE has
// no dedicated "strong" role yet — if one is added later, replace this
// inline recipe with `STATUS_PALETTE.error.strong` or equivalent.
const ERROR_STRONG = {
  bg: STATUS_PALETTE.error.light.color, // '#A32D2D' — saturated pure red
  text: '#FFFFFF',
  accent: '#FFFFFF',
};

export interface AgingBucketSideColors {
  bg: string;
  text: string;
  /** Left-border accent for cards; ignored by badges. */
  accent: string;
}

export interface AgingBucketColors {
  card: {
    light: AgingBucketSideColors;
    dark: AgingBucketSideColors;
  };
  badge: {
    light: { bg: string; text: string };
    dark: { bg: string; text: string };
  };
  /**
   * A colour that stands alone on the page background — not a pale pill fill, and not `card.accent`,
   * which for a `strong()` bucket is white (meant to sit on that bucket's own red card, not the
   * page). Used by the trend chart's column fill and legend swatch, the only place a bucket colour
   * is drawn directly on the page rather than inside a badge or card.
   */
  chart: { light: string; dark: string };
  /** i18n key used with `useLocales('operativaDashboard')` */
  labelKey: string;
}

const BUCKET_LABEL_KEYS: Record<AgingBucket, string> = {
  current: 'corrente',
  '1_30': '1_30_scaduto',
  '31_60': '31_60_scaduto',
  '60_plus': '60_plus_scaduto',
};

function regular(token: StatusToken, labelKey: string): AgingBucketColors {
  const pair = STATUS_PALETTE[token];
  return {
    card: {
      light: { bg: pair.light.bg, text: pair.light.color, accent: pair.light.accent },
      dark: { bg: pair.dark.bg, text: pair.dark.color, accent: pair.dark.accent },
    },
    badge: {
      light: { bg: pair.light.bg, text: pair.light.color },
      dark: { bg: pair.dark.bg, text: pair.dark.color },
    },
    // The saturated accent already reads fine directly on the page.
    chart: { light: pair.light.accent, dark: pair.dark.accent },
    labelKey,
  };
}

function strong(labelKey: string): AgingBucketColors {
  const side = { bg: ERROR_STRONG.bg, text: ERROR_STRONG.text, accent: ERROR_STRONG.accent };
  return {
    card: { light: side, dark: side },
    badge: {
      light: { bg: ERROR_STRONG.bg, text: ERROR_STRONG.text },
      dark: { bg: ERROR_STRONG.bg, text: ERROR_STRONG.text },
    },
    // `card.accent` is white here, built to sit on this bucket's own red card — invisible on the
    // page itself. The card's saturated `bg` (`#A32D2D`) is the colour that stands alone in
    // light mode (>=3:1 against both light page backgrounds), but not in dark mode: against the
    // dark page backgrounds it's ~2.2-2.5:1, below the WCAG 3:1 floor for graphical objects. Dark
    // mode uses a dedicated chart-only value instead — see `ERROR_STRONG_DARK_CHART` in
    // `status-palette.ts` for why no existing token fits (NAT-2610 review round 2).
    chart: { light: ERROR_STRONG.bg, dark: ERROR_STRONG_DARK_CHART },
    labelKey,
  };
}

export const AGING_BUCKET_CONFIG: Record<AgingBucket, AgingBucketColors> = {
  current: regular('neutral', BUCKET_LABEL_KEYS.current),
  '1_30': regular('warning', BUCKET_LABEL_KEYS['1_30']),
  '31_60': regular('error', BUCKET_LABEL_KEYS['31_60']),
  '60_plus': strong(BUCKET_LABEL_KEYS['60_plus']),
};

export const BUCKET_KEYS: AgingBucket[] = ['current', '1_30', '31_60', '60_plus'];
